import { useEffect, useState } from "react"
import { useAuth } from "@clerk/clerk-react"
import { Loader2 } from "lucide-react"
import { useParams } from "wouter"
import TopBar from "@/components/layout/TopBar"
import Sidebar from "@/components/layout/Sidebar"
import ProductionRail from "@/components/layout/ProductionRail"
import OverviewView from "@/pages/OverviewView"
import DevelopView from "@/pages/DevelopView"
import PlanView from "@/pages/PlanView"
import ProduceView from "@/pages/ProduceView"
import FinishView from "@/pages/FinishView"
import PublishView from "@/pages/PublishView"
import { useStudioStore } from "@/store/studio"
import CreditDialog from "@/components/CreditDialog"
import Toast from "@/components/Toast"
import { getProject } from "@/lib/api"
import type { Direction, Story } from "@/types"

export default function StudioPage() {
  const { getToken, isLoaded, isSignedIn } = useAuth()
  const params = useParams<{ tab?: string }>()
  const tab = params.tab || "home"
  const store = useStudioStore()
  const setView = store.setView
  const cloudProjectId = store.cloudProjectId
  const [hydrating, setHydrating] = useState(Boolean(store.cloudProjectId))
  const [hydrateError, setHydrateError] = useState("")

  useEffect(() => {
    setView(tab)
  }, [tab, setView])

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !cloudProjectId) { setHydrating(false); return }
    let cancelled = false
    setHydrating(true); setHydrateError("")
    getToken().then(async (token) => {
      if (!token || cancelled) return
      try {
        const result = await getProject(cloudProjectId, token)
        if (cancelled) return
        const hydratedStore = useStudioStore.getState()
        hydratedStore.setProject(cloudProjectId, result.project.title, result.project.concept || "")
        const latest = (kind: string) => result.documents.find((document) => document.kind === kind)
        const directionsDocument = latest("creative-directions")
        if (directionsDocument) {
          const content = JSON.parse(directionsDocument.content_json) as { directions?: Direction[] }
          if (Array.isArray(content.directions)) hydratedStore.setDirections(content.directions)
        }
        const storyDocument = latest("story-draft")
        if (storyDocument) {
          const story = JSON.parse(storyDocument.content_json) as Story
          hydratedStore.setStory(story)
          hydratedStore.setStoryHash(storyDocument.content_hash)
          hydratedStore.setStoryApproved(result.approvals.some((approval) => approval.subject_hash === storyDocument.content_hash && approval.decision === "approved"))
          hydratedStore.setStage(Math.max(hydratedStore.stage, 1))
        }
        const continuityDocument = latest("production-bible")
        hydratedStore.setContinuityPreparation(result.latestContinuityPreparation)
        if (continuityDocument) {
          const continuity = JSON.parse(continuityDocument.content_json) as { bible?: unknown }
          const approved = result.approvals.some((approval) => approval.subject_hash === continuityDocument.content_hash && approval.decision === "approved")
          const readyForReview = result.latestContinuityPreparation?.status === "completed"
            && result.latestContinuityPreparation.id === continuityDocument.id
          if ((approved || readyForReview) && continuity.bible) hydratedStore.setContinuityLock({
            revisionId: continuityDocument.id,
            hash: continuityDocument.content_hash,
            referencePath: `/v1/projects/${cloudProjectId}/continuity/${continuityDocument.id}/reference`,
            bible: continuity.bible as NonNullable<ReturnType<typeof useStudioStore.getState>["continuityLock"]>["bible"],
          })
        }
        // A freshly approved Plan deliberately starts a new production cycle.
        // Do not let an older completed batch overwrite that clean local gate;
        // once a new batch exists, its persisted id reconnects normally.
        const shouldReconnectBatch = Boolean(result.latestBatch)
          && (!hydratedStore.planApproved || hydratedStore.batchRunId === result.latestBatch?.id)
        if (result.latestBatch && shouldReconnectBatch) {
          const batchStatus = result.latestBatch.status === "cancelled" ? "failed" : result.latestBatch.status
          hydratedStore.setBatchStatus(result.latestBatch.id, batchStatus)
          hydratedStore.setRenderProgress({ completed: result.latestBatch.completed_shots, total: result.latestBatch.total_shots, etaSeconds: 0, confidence: 0.5 })
          if (batchStatus === "completed") hydratedStore.setStage(Math.max(useStudioStore.getState().stage, 3))
        } else {
          hydratedStore.setBatchStatus("", "pending")
          hydratedStore.setRenderProgress({ completed: 0, total: 0, etaSeconds: 0, confidence: 0 })
        }
        hydratedStore.setRenderJob(
          shouldReconnectBatch ? result.latestAssembly?.job.id || "" : "",
          shouldReconnectBatch ? result.latestAssembly?.job.status || "" : "",
          shouldReconnectBatch ? result.latestAssembly?.artifact || null : null,
        )
      } catch (reason) {
        if (!cancelled) setHydrateError(reason instanceof Error ? reason.message : "This project could not be restored.")
      } finally { if (!cancelled) setHydrating(false) }
    })
    return () => { cancelled = true }
  }, [cloudProjectId, getToken, isLoaded, isSignedIn])

  const renderView = () => {
    switch (tab) {
      case "home":
      case "":
        return <OverviewView />
      case "develop":
        return <DevelopView />
      case "plan":
        return <PlanView />
      case "produce":
        return <ProduceView />
      case "finish":
        return <FinishView />
      case "publish":
        return <PublishView />
      default:
        return <OverviewView />
    }
  }

  return (
    <div className="min-h-screen bg-[#08090d]">
      <TopBar />
      <div className="flex min-w-0">
        <Sidebar />
        <main id="studio-workspace" className="flex-1 min-w-0 min-h-[calc(100vh-3.5rem)] pb-20 lg:pb-0">
          <ProductionRail />
          <div className="px-3 py-4 sm:p-5 xl:p-6 max-w-[1400px] mx-auto">
            {hydrating ? <div className="surface min-h-[300px] flex items-center justify-center text-center p-8"><div><Loader2 className="w-5 h-5 mx-auto mb-3 animate-spin text-[#f59e0b]" /><p className="text-sm text-[#f0f1f5]">Restoring this production…</p><p className="text-xs text-[#6b7280] mt-1">Loading the latest story, approvals and render progress.</p></div></div> : hydrateError ? <div className="surface min-h-[260px] flex items-center justify-center text-center p-8"><div><p className="text-sm text-red-300">{hydrateError}</p><button onClick={() => window.location.reload()} className="btn-subtle mt-4">Try again</button></div></div> : renderView()}
          </div>
        </main>
      </div>
      <CreditDialog />
      <Toast />
    </div>
  )
}
